import { Trans } from '@lingui/macro'
import { ReactComponent as LongIcon } from 'assets/svg/swap/long.svg'
import { ReactComponent as ShortIcon } from 'assets/svg/swap/short.svg'
import Column from 'components/Column'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import { IconLoadingBubble, LoadingBubble, MediumLoadingBubble } from 'components/Loading'
import Row from 'components/Row'
import { StyledTableRow } from 'components/Table'
import { MAX_WIDTH_MEDIA_BREAKPOINT, SMALL_MEDIA_BREAKPOINT } from 'components/Tokens/constants'
import { useCurrency } from 'hooks/Tokens'
import { useTradeRouter } from 'hooks/useContract'
import { TextLeftCell } from 'pages/Lock/LockRow'
import { CSSProperties, ForwardedRef, forwardRef, ReactNode, useMemo } from 'react'
import { Box } from 'rebass'
import styled, { css, useTheme } from 'styled-components/macro'
import { ThemedText } from 'theme'
import { BN, fromWei } from 'utils/bn'
import { countZeros } from 'utils/countZeros'

import { ExecuteBtn, StyledBonus } from '../LimitList/LimitRow'
import { LiquidationSortMethod } from './state'

const StyledLiquidationRow = styled(StyledTableRow)<{
  first?: boolean
  last?: boolean
  $loading?: boolean
}>`
  align-items: center;
  grid-template-columns: 200px 1fr 1fr 1fr 1fr 0.8fr;
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  min-width: 390px;
  padding: 0;
  margin: 0;
  ${({ first, last }) => css`
    /* height: ${first || last ? '72px' : '64px'}; */
  `}
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
  transition-duration: ${({ theme }) => theme.transition.duration.fast};

  &:hover {
    ${({ $loading, theme }) =>
      !$loading &&
      css`
        background-color: ${theme.hoverDefault};
      `}
    ${({ last }) =>
      last &&
      css`
        border-radius: 0px 0px 8px 8px;
      `}
  }
  .bg {
    height: 100%;
    ${({ theme }) =>
      css`
        background-color: ${theme.hoverDefault};
      `}
  }
`

const StyledTrRow = styled(StyledLiquidationRow)``

const StyledHeaderRow = styled(StyledLiquidationRow)`
  height: 40px;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;

  &:hover {
    background-color: transparent;
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    justify-content: space-between;
  }
`

/* Token Row: skeleton row component */
function LiquidationRow({
  header,
  positions,
  liqPrice,
  markPrice,
  size,
  bonus,
  action,
  ...rest
}: {
  first?: boolean
  header: boolean
  $loading?: boolean
  size: ReactNode
  liqPrice?: ReactNode
  markPrice: ReactNode
  positions: ReactNode
  bonus: ReactNode
  action: ReactNode
  last?: boolean
  style?: CSSProperties
}) {
  const rowHeaderCells = (
    <>
      <TextLeftCell data-testid="positions-cell">{positions}</TextLeftCell>
      <TextLeftCell data-testid="trigger-price-cell">{liqPrice}</TextLeftCell>
      <TextLeftCell data-testid="mark-price-cell">{markPrice}</TextLeftCell>
      <TextLeftCell data-testid="size-cell">{size}</TextLeftCell>
      <TextLeftCell data-testid="bonus-cell">{bonus}</TextLeftCell>
      <TextLeftCell data-testid="total-rewards-cell">{action}</TextLeftCell>
    </>
  )
  const rowTrCells = (
    <>
      <TextLeftCell data-testid="positions-cell">
        <Box p="16px 0" width="100%" height="100%">
          {positions}
        </Box>
      </TextLeftCell>
      <TextLeftCell data-testid="trigger-price-cell">
        <Box p="16px 0" width="100%" height="100%">
          {liqPrice}
        </Box>
      </TextLeftCell>
      <TextLeftCell data-testid="mark-price-cell">
        <Box p="16px 0" width="100%" height="100%">
          {markPrice}
        </Box>
      </TextLeftCell>
      <TextLeftCell data-testid="size-cell">
        <Box p="16px 0" width="100%" height="100%">
          {size}
        </Box>
      </TextLeftCell>
      <TextLeftCell data-testid="bonus-cell">
        <Box p="16px 0" width="100%" height="100%">
          {bonus}
        </Box>
      </TextLeftCell>
      <TextLeftCell data-testid="total-rewards-cell">
        <Box p="16px 0" width="100%" height="100%">
          {action}
        </Box>
      </TextLeftCell>
    </>
  )
  if (header) return <StyledHeaderRow data-testid="liquidation-header-row">{rowHeaderCells}</StyledHeaderRow>
  return <StyledTrRow {...rest}>{rowTrCells}</StyledTrRow>
}

/* Header Row: top header row component for table */
export function HeaderRow() {
  return (
    <LiquidationRow
      header={true}
      positions={<Trans>{LiquidationSortMethod.Positions}</Trans>}
      liqPrice={<Trans>{LiquidationSortMethod.LiqPrice}</Trans>}
      markPrice={<Trans>{LiquidationSortMethod.MarkPrice}</Trans>}
      size={<Trans>{LiquidationSortMethod.Size}</Trans>}
      bonus={<Trans>{LiquidationSortMethod.Bonus}</Trans>}
      action={<Trans>{LiquidationSortMethod.Action}</Trans>}
    />
  )
}
/* Loading State: row component with loading bubbles */
export function LoadingRow(props: { first?: boolean; last?: boolean }) {
  return (
    <LiquidationRow
      header={false}
      $loading
      positions={
        <Row gap="xs">
          <IconLoadingBubble width="40px" height="40px" />
          <MediumLoadingBubble />
        </Row>
      }
      liqPrice={
        <Column align="flex-start" width="100%" gap="xs">
          <MediumLoadingBubble />
        </Column>
      }
      markPrice={
        <Row justify="flex-start" gap="xs">
          <LoadingBubble />
        </Row>
      }
      size={
        <Column align="flex-start" width="100%" gap="xs">
          <MediumLoadingBubble />
        </Column>
      }
      bonus={
        <Column align="flex-start" width="100%" gap="xs">
          <MediumLoadingBubble />
        </Column>
      }
      action={
        <Column align="flex-start" width="100%" gap="xs">
          <MediumLoadingBubble />
        </Column>
      }
      {...props}
    />
  )
}

export interface LoadedRowProps {
  token01: boolean
  liquidationListIndex: number
  liquidationListLength: number
  liquidation: any
}

/* Loaded State: row component with token information */
export const LoadedRow = forwardRef((props: LoadedRowProps, ref: ForwardedRef<HTMLDivElement>) => {
  const { liquidationListIndex, liquidationListLength, liquidation, token01 } = props
  const theme = useTheme()
  const currency0 = useCurrency(liquidation.v3Pool.token0.id)
  const currency1 = useCurrency(liquidation.v3Pool.token1.id)
  const decimals = liquidation.long0 ? currency1?.decimals : currency0?.decimals
  const feesymbol = liquidation.long0 ? currency0?.symbol : currency1?.symbol
  const feesdecimals = liquidation.long0 ? currency0?.decimals : currency1?.decimals
  const sizeymbol = liquidation.long0 ? currency1?.symbol : currency0?.symbol

  const lever = useMemo(() => {
    const collateral = liquidation.collateral
    return BN(liquidation.size).div(collateral)
  }, [liquidation.collateral, liquidation.size])

  const routerContract = useTradeRouter()
  const twapPrice = liquidation.twapPrice

  const canExec = useMemo(() => {
    if(liquidation.isLiq) return true
    if (!twapPrice) {
      return false
    }
    if (token01) {
      const execPirce = liquidation.close0Price
      if (!liquidation.long0) {
        return twapPrice >= execPirce
      } else {
        return twapPrice <= execPirce
      }
    } else {
      const execPirce = 1 / liquidation.close0Price
      if (liquidation.long0) {
        return 1 / twapPrice >= execPirce
      } else {
        return 1 / twapPrice <= execPirce
      }
    }
  }, [liquidation, token01, twapPrice])

  const handleEecu = async () => {
    try {
      await routerContract?.liquidatePosition(liquidation.prepPoolAddress, liquidation.key)
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <>
      <div ref={ref} data-testid={`liquidation-table-row-${liquidation.lpAddr}`}>
        <LiquidationRow
          header={false}
          positions={
            <Row>
              <DoubleCurrencyLogo
                size={40}
                margin
                currency0={token01 ? currency1 ?? undefined : currency0 ?? undefined}
                currency1={token01 ? currency0 ?? undefined : currency1 ?? undefined}
              />
              <Column gap="xs" align="flex-start" ml="16px">
                <ThemedText.TextPrimary fontWeight={500} fontSize={16}>
                  {token01 ? `${currency0?.symbol}/${currency1?.symbol}` : `${currency1?.symbol}/${currency0?.symbol}`}
                </ThemedText.TextPrimary>
                <Row gap="xs">
                  <ThemedText.TextSecondary fontWeight={700} fontSize={14}>
                    {lever.toFixed(2)}x
                  </ThemedText.TextSecondary>
                  {token01 ? (
                    liquidation.long0 ? (
                      <LongIcon />
                    ) : (
                      <ShortIcon />
                    )
                  ) : !liquidation.long0 ? (
                    <LongIcon />
                  ) : (
                    <ShortIcon />
                  )}
                </Row>
              </Column>
            </Row>
          }
          liqPrice={
            <Column align="flex-start" width="100%" gap="xs">
              <ThemedText.TextPrimary fontWeight={500} fontSize={16}>
                {token01 ? (liquidation.long0 ? '<' : '>') : !liquidation.long0 ? '<' : '>'}{' '}
                {countZeros(token01 ? liquidation.close0Price : liquidation.close0Price == 0 ? 0 : 1 / liquidation.close0Price)}
              </ThemedText.TextPrimary>
            </Column>
          }
          markPrice={
            <Row justify="flex-start" gap="xs">
              <ThemedText.TextPrimary fontWeight={500} fontSize={16}>
                {countZeros(token01 ? twapPrice : 1 / twapPrice)}
              </ThemedText.TextPrimary>
            </Row>
          }
          size={
            <Column align="flex-start" width="100%" gap="xs">
              <ThemedText.TextSecondary>
                {countZeros(fromWei(liquidation.size, decimals).toFixed())} {sizeymbol}
              </ThemedText.TextSecondary>
            </Column>
          }
          bonus={
            <Column align="flex-start" width="100%" gap="xs">
              <StyledBonus className={`${theme.darkMode ? 'active-gradient-text-dark' : 'active-gradient-text'}`}>
                {countZeros(fromWei(liquidation.liqResv, feesdecimals).toFixed())} {feesymbol}
              </StyledBonus>
            </Column>
          }
          action={
            <Column align="flex-start" gap="xs">
              <ExecuteBtn onClick={handleEecu} disabled={!canExec}>
                <Trans>{canExec ? 'Execute' : 'Not available'}</Trans>
              </ExecuteBtn>
            </Column>
          }
          first={liquidationListIndex === 0}
          last={liquidationListIndex === liquidationListLength - 1}
        />
      </div>
    </>
  )
})

LoadedRow.displayName = 'LoadedRow'
